import { makeStyles } from "tss-react/mui";
import { Box, GridLegacy, Typography } from "@mui/material";
import { DataGridPremium, gridClasses } from "@mui/x-data-grid-premium";

/**
 * A reusable component for rendering printable sections with customizable tables, columns, and rows.
 * This component supports dynamic rendering of data grids, columns, and rows, and is highly customizable
 * through props such as `title`, `columns`, `rowTables`, and `tables`.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {string} [props.title] - The title of the section. Optional.
 * @param {string} [props.width="100%"] - The width of the section. Defaults to "100%".
 * @param {number} [props.columnHeaderHeight=24] - The height of the column headers in the data grid. Defaults to 24.
 * @param {number} [props.rowHeight=58] - The height of the rows in the data grid. Defaults to 58.
 * @param {Function} [props.getCellClassName] - A function to dynamically apply class names to grid cells. Optional.
 * @param {Array<Object>} [props.tables=[]] - An array of table configurations. Each table object should contain `field`, `rows`, and `columns`. Optional.
 * @param {Array<Array<Object>>} [props.columns=[]] - An array of column configurations. Each column contains an array of row objects with `label` and `value`. Optional.
 * @param {Array<Array<Object>>} [props.rowTables=[]] - An array of row table configurations. Each row table contains an array of row objects with `label` and `value`. Optional.
 * @param {Object} [props.style] - Additional inline styles to apply to the root container. Optional.
 * @returns {JSX.Element} The rendered printable section component.
 *
 * @example
 * // Example usage of MiPrintSection
 * <MiPrintSection
 *   title="Sample Section"
 *   width="90%"
 *   columnHeaderHeight={30}
 *   rowHeight={60}
 *   tables={[
 *     {
 *       field: "table1",
 *       rows: [
 *         [{ label: "Name", value: "John Doe" }, { label: "Age", value: "30" }],
 *         [{ label: "Name", value: "Jane Doe" }, { label: "Age", value: "25" }],
 *       ],
 *       columns: [
 *         { label: "Name", headerAlign: "left", align: "left" },
 *         { label: "Age", headerAlign: "center", align: "center" },
 *       ],
 *     },
 *   ]}
 *   columns={[
 *     [
 *       { label: "Field 1", value: "Value 1" },
 *       { label: "Field 2", value: "Value 2" },
 *     ],
 *   ]}
 *   rowTables={[
 *     [
 *       { label: "Row 1", value: "Data 1" },
 *       { label: "Row 2", value: "Data 2" },
 *     ],
 *   ]}
 * />
 */
export const MiPrintSection = ({
  title,
  width = "100%",
  columnHeaderHeight = 24,
  rowHeight,
  getCellClassName,
  tables = [],
  columns = [],
  rowTables = [],
  style,
}) => {
  const { classes } = useStyles({ cellMinHeight: rowHeight ?? 58 });

  const renderRows = (rows) =>
    rows.map(({ label, value }) => (
      <Box key={label} className={classes.fieldColumn}>
        <Typography className={classes.fieldLabel} fontSize="inherit">
          {label}
        </Typography>
        <Typography fontSize="inherit">{value}</Typography>
      </Box>
    ));

  const renderColumns = () =>
    columns.map((rows, i) => (
      <GridLegacy
        item
        xs={12 / columns.length}
        key={i}
        className={classes.column}
      >
        {renderRows(rows)}
      </GridLegacy>
    ));

  const renderRowTables = () =>
    rowTables.map((rowData, i) => (
      <Box key={i} maxWidth="100%">
        <DataGridPremium
          key={i}
          className={classes.dataGrid}
          columnHeaderHeight={columnHeaderHeight}
          getRowHeight={() => rowHeight ?? "auto"}
          getCellClassName={getCellClassName}
          sx={{
            minHeight: (rowHeight ?? 58) + columnHeaderHeight,
          }}
          rows={[
            {
              id: i,
              ...Object.fromEntries(
                rowData.map(({ label, value }) => [label, value])
              ),
            },
          ]}
          columns={rowData.map(
            ({
              label,
              headerAlign = "center",
              align = "center",
              textWrappping = true,
              ...props
            }) => ({
              field: label,
              headerName: label,
              headerAlign,
              align,
              textWrappping,
              ...props,
              headerClassName: classes.tableHeaderCell,
              cellClassName: classes.tableCell,
            })
          )}
          hideFooter
        />
      </Box>
    ));

  const renderTables = () =>
    tables.map(({ field, rows, columns }, i) =>
      rows.length ? (
        <Box key={field || i} maxWidth="100%">
          <DataGridPremium
            className={classes.dataGrid}
            columnHeaderHeight={columnHeaderHeight}
            getRowHeight={() => rowHeight ?? "auto"}
            getCellClassName={getCellClassName}
            sx={{
              minHeight: rows.length * (rowHeight ?? 58) + columnHeaderHeight,
            }}
            rows={rows.map((row, index) => ({
              id: index,
              ...Object.fromEntries(
                row.map(({ label, value }) => [label, value])
              ),
            }))}
            columns={
              columns.map(
                ({
                  label,
                  headerAlign = "center",
                  align = "center",
                  textWrappping = true,
                  ...props
                }) => ({
                  field: label,
                  headerName: label,
                  headerAlign,
                  align,
                  textWrappping,
                  ...props,
                  headerClassName: classes.tableHeaderCell,
                  cellClassName: classes.tableCell,
                })
              ) || []
            }
            hideFooter
          />
        </Box>
      ) : null
    );

  return (
    <Box className={classes.root} width={width} style={style}>
      {title && (
        <Box className={classes.titleSection}>
          <Typography fontSize="inherit" fontWeight="inherit">
            {title}
          </Typography>
        </Box>
      )}
      {columns.length ? (
        <GridLegacy container>{renderColumns()}</GridLegacy>
      ) : null}
      {rowTables.length ? (
        <GridLegacy container className={classes.tableContainer}>
          {renderRowTables()}
          {renderTables()}
        </GridLegacy>
      ) : null}
    </Box>
  );
};

const useStyles = makeStyles()((theme, { cellMinHeight }) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  titleSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.blue.light,
    height: 24,
    zIndex: 1,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(5),
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.typography.pxToRem(14),
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  fieldColumn: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: theme.spacing(1.5),
    fontSize: theme.typography.pxToRem(14),
    zIndex: 1,
  },
  fieldLabel: {
    color: theme.palette.common.black,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.typography.pxToRem(14),
  },
  dataGrid: {
    borderRadius: 0,
    width: "100%",
    "*": {
      overflow: "hidden !important",
    },
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [`.${gridClasses.columnHeader}`]: {
      backgroundColor: theme.palette.grey.lightAccent,
    },
    [`.${gridClasses.cell}.grey`]: {
      backgroundColor: theme.palette.grey["100"],
    },
    [`.${gridClasses.cell}.blue`]: {
      backgroundColor: theme.palette.blue.light,
      color: theme.palette.blue.dark,
    },
    [`.${gridClasses.cell}.bold`]: {
      fontWeight: theme.fontWeight.black,
    },
  },
  tableHeaderCell: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.fontWeight.medium,
  },
  tableCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(13.5),
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
    wordBreak: "break-word",
    minHeight: cellMinHeight,
    borderRight: `1px solid ${theme.palette.grey.accent}`,
  },
}));
